import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, Button, FlatButton } from '@reservamos/elements';
import ModalWithTheme from 'components/ModalWithTheme';

// The seat edit is the only action left that can cost the buyer their code, so this prompt carries
// one copy rather than selecting between several. It removes nothing: `purchase.discount` survives
// a re-lock — no caller of `Purchase#remove_discount!` sits in that path — but the re-lock re-quotes
// ticket pricing against the *search* coupon rather than `purchase.discount.code`, so the code stops
// being reflected in the price while staying applied. "Apply it again" would not just be useless, it
// fails: `Discounts::Validations#invalid_discount?` refuses ANY code while the purchase already
// carries one, so following that advice returns an invalid-coupon banner and clears the input.
//
// The passenger prompts this component used to select between are gone. In Quick Checkout a
// passenger save can only carry identity fields, so it cannot move the total, and BOO-114 made
// `drop_discount_if_invalidated` release a coupon only when the new total misses its minimum.
const COPY_KEY = 'quick_checkout.discount_loss.seats';

/**
 * QuickCheckoutDiscountLossModal component.
 * Warns the buyer before a seat edit stops their applied code from being reflected in the price,
 * and lets them back out. Without it the price moves with nothing connecting it to what they did.
 * @param {object} props - Component props.
 * @param {Function} props.onConfirm - Proceed with the edit.
 * @param {Function} props.onCancel - Leave the purchase and its discount untouched.
 * @returns {JSX.Element} The confirmation modal.
 */
const QuickCheckoutDiscountLossModal = ({ onConfirm, onCancel }) => {
  const { t } = useTranslation('purchase');

  return (
    <ModalWithTheme
      centeredOnMobile
      responsiveSize="auto"
      borderRadiusBigger
      contentHeight="auto"
      onCloseModal={onCancel}
    >
      <Spacing vertical>
        <Spacing vertical size="S">
          <Text size="L" weight="bold" textAlign="center">
            {t(`${COPY_KEY}.title`)}
          </Text>
          <Text textAlign="center">{t(`${COPY_KEY}.description`)}</Text>
        </Spacing>
        <Spacing justifyContent="space-between">
          <Button
            isRounded
            padding="XL"
            text={t(`${COPY_KEY}.cancel`)}
            variant="primary"
            onClick={onCancel}
          />
          <FlatButton text={t(`${COPY_KEY}.confirm`)} type="primary" onClick={onConfirm} />
        </Spacing>
      </Spacing>
    </ModalWithTheme>
  );
};

QuickCheckoutDiscountLossModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default QuickCheckoutDiscountLossModal;
